import { Fragment, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { UserDetail, UserSummary } from '../api/types';
import { getRuntimeConfig } from '../runtimeConfig';
import { useApp } from '../state/AppContext';
import { photoSrc } from '../utils/photo';
import { distinctTags, tagsOf } from '../utils/tags';
import { PhotoPicker, type PhotoChange } from './PhotoPicker';
import { TagInput } from './TagInput';

type Mode = { kind: 'create' } | { kind: 'edit'; user: UserDetail };

interface UserFormModalProps {
  mode: Mode;
  onClose: () => void;
  onSaved: (message: string, created?: UserSummary) => void;
}

interface FieldDef {
  key: string;
  label: string;
  type?: 'text' | 'email' | 'date';
  required?: boolean;
  wide?: boolean;
}

const FIELDS: FieldDef[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email', type: 'email', required: true, wide: true },
  { key: 'title', label: 'Position' },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'location', label: 'Location' },
  { key: 'phone', label: 'Telephone' },
  { key: 'personalEmail', label: 'Personal email', type: 'email' },
  { key: 'personalPhone', label: 'Personal phone' },
  { key: 'address', label: 'Address', wide: true },
  { key: 'companyStart', label: 'Company start', type: 'date' },
  { key: 'companyLeave', label: 'Company leave', type: 'date' },
];

function initialValues(mode: Mode): Record<string, string> {
  const base: Record<string, string> = { username: '' };
  for (const field of FIELDS) {
    base[field.key] = '';
  }
  if (mode.kind === 'edit') {
    const u = mode.user;
    base.username = u.username;
    base.firstName = u.firstName ?? '';
    base.lastName = u.lastName ?? '';
    base.email = u.email ?? '';
    base.title = u.title ?? '';
    base.department = u.department ?? '';
    base.manager = u.manager ?? '';
    base.location = u.location ?? '';
    base.phone = u.phone ?? '';
    base.personalEmail = u.personalEmail ?? '';
    base.personalPhone = u.personalPhone ?? '';
    base.address = u.address ?? '';
    base.companyStart = u.companyStart ?? '';
    base.companyLeave = u.companyLeave ?? '';
  }
  return base;
}

function errorText(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function UserFormModal({ mode, onClose, onSaved }: UserFormModalProps) {
  const { users } = useApp();
  const { apiUrl } = getRuntimeConfig();
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(mode));
  const [tags, setTags] = useState<string[]>(() =>
    mode.kind === 'edit' ? tagsOf(mode.user) : [],
  );
  const [photo, setPhoto] = useState<PhotoChange>({ kind: 'unchanged' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allTags = useMemo(() => distinctTags(users), [users]);

  const isCreate = mode.kind === 'create';
  const set = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const canSubmit =
    !submitting &&
    values.email.trim().length > 0 &&
    (!isCreate || values.username.trim().length > 0);

  const currentPhoto =
    mode.kind === 'edit' ? photoSrc(apiUrl, mode.user.id, mode.user.photoVersion) : null;
  const avatarName =
    mode.kind === 'edit'
      ? mode.user.name
      : `${values.firstName} ${values.lastName}`.trim() || values.username.trim() || 'New user';

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      if (mode.kind === 'create') {
        const result = await api.createUser({
          username: values.username.trim(),
          email: values.email.trim(),
          firstName: values.firstName.trim() || null,
          lastName: values.lastName.trim() || null,
          department: values.department.trim() || null,
          title: values.title.trim() || null,
          manager: values.manager.trim() || null,
          location: values.location.trim() || null,
          phone: values.phone.trim() || null,
          personalEmail: values.personalEmail.trim() || null,
          personalPhone: values.personalPhone.trim() || null,
          address: values.address.trim() || null,
          companyStart: values.companyStart.trim() || null,
          companyLeave: values.companyLeave.trim() || null,
          tags: tags.length > 0 ? tags : null,
        });
        let created = result.user;
        let photoWarning: string | null = null;
        if (photo.kind === 'replace') {
          try {
            const uploaded = await api.uploadUserPhoto(result.user.id, photo.blob);
            created = { ...created, photoVersion: uploaded.photoVersion };
          } catch (cause) {
            photoWarning = errorText(cause);
          }
        }
        const base = result.emailSent
          ? `Temporary password emailed to ${values.email.trim()}`
          : `User created, but the email failed: ${result.warning ?? 'unknown error'}`;
        onSaved(photoWarning ? `${base} · photo upload failed: ${photoWarning}` : base, created);
      } else {
        await api.updateUser(mode.user.id, {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          department: values.department.trim(),
          title: values.title.trim(),
          manager: values.manager.trim(),
          location: values.location.trim(),
          phone: values.phone.trim(),
          personalEmail: values.personalEmail.trim(),
          personalPhone: values.personalPhone.trim(),
          address: values.address.trim(),
          companyStart: values.companyStart.trim(),
          companyLeave: values.companyLeave.trim(),
          tags,
        });
        try {
          if (photo.kind === 'replace') {
            await api.uploadUserPhoto(mode.user.id, photo.blob);
          } else if (photo.kind === 'remove') {
            await api.deleteUserPhoto(mode.user.id);
          }
        } catch (cause) {
          setError(`Profile saved, but the photo failed: ${errorText(cause)}`);
          setSubmitting(false);
          return;
        }
        onSaved(`Profile updated for ${mode.user.name}`);
      }
    } catch (cause) {
      setError(errorText(cause));
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isCreate ? 'New user' : 'Edit profile'}</div>
          <div className="modal-subtitle">
            {isCreate ? (
              'Creates a Keycloak account and emails a temporary password the user must change at the first sign-in.'
            ) : (
              <>
                Update the profile of <strong>{mode.user.name}</strong>.
              </>
            )}
          </div>
        </div>
        <div className="modal-body">
          <PhotoPicker
            name={avatarName}
            currentSrc={currentPhoto}
            value={photo}
            onChange={setPhoto}
            disabled={submitting}
          />
          <div className="form-grid">
            {isCreate && (
              <label className="form-field form-field-wide">
                <span className="form-label">
                  Username <span className="form-req">*</span>
                </span>
                <input
                  className="form-input"
                  value={values.username}
                  onChange={(event) => set('username', event.target.value)}
                  placeholder="jane.doe"
                  autoFocus
                />
              </label>
            )}
            {FIELDS.map((field) => (
              <Fragment key={field.key}>
                <label className={`form-field${field.wide ? ' form-field-wide' : ''}`}>
                  <span className="form-label">
                    {field.label}
                    {field.required && <span className="form-req"> *</span>}
                  </span>
                  <input
                    className="form-input"
                    type={field.type ?? 'text'}
                    value={values[field.key]}
                    onChange={(event) => set(field.key, event.target.value)}
                  />
                </label>
                {field.key === 'department' && (
                  <div className="form-field form-field-wide">
                    <span className="form-label">Tags</span>
                    <TagInput
                      value={tags}
                      onChange={setTags}
                      suggestions={allTags}
                      placeholder="Type to pick an existing tag or add a new one…"
                    />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
          {error && <div className="modal-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {submitting
              ? isCreate
                ? 'Creating…'
                : 'Saving…'
              : isCreate
                ? 'Create & email password'
                : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
